import { collection, query, where, orderBy, limit, startAfter, getDocs, getDoc, doc, addDoc, updateDoc, deleteDoc, QueryDocumentSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import { User } from '@/types/users';
import { isDemoWorkspace } from '@/utils/apiHelpers';
import { loadMockData } from '@/mock';

export interface ExpenseItem {
  id: string;
  description: string;
  category: 'utilities' | 'maintenance' | 'security' | 'administrative' | 'other';
  amount: number;
  date: any; // Dayjs object
}

export interface ExpenseReport {
  id: string;
  month: number; // 1-12
  year: number;
  title: string;
  summary?: string;
  totalAmount: number;
  fileUrl?: string;
  fileName?: string;
  items: ExpenseItem[];
  publishedDate: any; // Dayjs object
}

export interface ExpensesQueryParams {
  firestoreUser: User;
  pageSize?: number;
  lastDoc?: QueryDocumentSnapshot;
  searchTerm?: string;
  year?: number;
  month?: number;
  page?: number;
}

// Helper functions for mock data
function filterMockData(
  data: ExpenseReport[], 
  searchTerm?: string, 
  year?: number, 
  month?: number
): ExpenseReport[] {
  return data.filter(report => {
    const matchesSearch = !searchTerm || 
      report.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.summary?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesYear = !year || report.year === year;
    const matchesMonth = !month || report.month === month;
    
    return matchesSearch && matchesYear && matchesMonth;
  });
}

function paginateMockData<T>(data: T[], pageSize?: number, currentPage: number = 1): { data: T[]; total: number } {
  const size = pageSize || 10;
  const startIndex = (currentPage - 1) * size;
  const endIndex = startIndex + size;
  const paginatedData = data.slice(startIndex, endIndex);
  
  return {
    data: paginatedData,
    total: data.length
  };
}

export async function getExpenseReports({ 
  firestoreUser, 
  pageSize = 10, 
  lastDoc, 
  searchTerm, 
  year, 
  month,
  page = 1 
}: ExpensesQueryParams): Promise<{ reports: ExpenseReport[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Debug: Log user workspace info
  console.log('🔍 Debug - getExpenseReports called with:', {
    firestoreUser: firestoreUser ? {
      id: firestoreUser.id,
      email: firestoreUser.email,
      workspace: firestoreUser.workspace
    } : null,
    isDemo: isDemoWorkspace(firestoreUser)
  });

  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('DEMO MODE: Fetching mock expense reports with filters', { searchTerm, year, month, page, pageSize });
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Load language-specific mock data
    const mockExpenseReports = await loadMockData<ExpenseReport[]>('expenses', firestoreUser.language);
    console.log('🔍 Debug - Mock expense reports loaded:', mockExpenseReports.length);
    
    const filteredData = filterMockData(mockExpenseReports, searchTerm, year, month);
    const paginatedData = paginateMockData(filteredData, pageSize, page);
    
    return { 
      reports: paginatedData.data, 
      lastDoc: undefined, 
      total: paginatedData.total 
    };
  }

  console.log('API Call: getExpenseReports', { pageSize, lastDoc, firestoreUser });
  
  try {
    const workspaceId = firestoreUser.workspace?.id;
    if (!workspaceId) {
      throw new Error('No workspace access');
    }

    const expensesRef = collection(db, 'workspaces', workspaceId, 'expenses');
    let q = query(expensesRef, orderBy('publishedDate', 'desc'));

    if (searchTerm) {
      // Note: Firestore doesn't support full-text search, so this is a simplified approach
      // In a real app, you might use Algolia or similar for better search
      q = query(q, where('title', '>=', searchTerm), where('title', '<=', searchTerm + '\uf8ff'));
    }

    if (year) {
      q = query(q, where('year', '==', year));
    }

    if (month) {
      q = query(q, where('month', '==', month));
    }

    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    q = query(q, limit(pageSize));
    const snapshot = await getDocs(q);

    const reports: ExpenseReport[] = [];
    snapshot.forEach(doc => {
      reports.push({ id: doc.id, ...doc.data() } as ExpenseReport);
    });

    return { 
      reports, 
      lastDoc: snapshot.docs[snapshot.docs.length - 1], 
      total: reports.length 
    };
  } catch (error) {
    console.error('Error fetching expense reports:', error);
    throw error;
  }
}

export async function getExpenseReportById(id: string, firestoreUser: User): Promise<ExpenseReport | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log(`DEMO MODE: Fetching mock expense report with id: ${id}`);
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Load language-specific mock data
    const mockExpenseReports = await loadMockData<ExpenseReport[]>('expenses', firestoreUser.language);
    const report = mockExpenseReports.find(r => r.id === id) || null;
    return report || null;
  }

  console.log('API Call: getExpenseReportById', { id, firestoreUser });
  
  try {
    const workspaceId = firestoreUser.workspace?.id;
    if (!workspaceId) {
      throw new Error('No workspace access');
    }

    const reportRef = doc(db, 'workspaces', workspaceId, 'expenses', id);
    const reportDoc = await getDoc(reportRef);

    if (reportDoc.exists()) {
      return { id: reportDoc.id, ...reportDoc.data() } as ExpenseReport;
    }

    return null;
  } catch (error) {
    console.error('Error fetching expense report:', error);
    throw error;
  }
}

export async function createExpenseReport(report: Omit<ExpenseReport, 'id'>, firestoreUser: User): Promise<string> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('Demo mode: Creating mock expense report');
    await new Promise(resolve => setTimeout(resolve, 300));
    const newId = `EXP${Date.now()}`;
    console.log('Mock expense report created with ID:', newId);
    return newId;
  }

  console.log('API Call: createExpenseReport', { firestoreUser });
  
  try {
    const workspaceId = firestoreUser.workspace?.id;
    if (!workspaceId) {
      throw new Error('No workspace access');
    }

    const expensesRef = collection(db, 'workspaces', workspaceId, 'expenses');
    const docRef = await addDoc(expensesRef, report);
    return docRef.id;
  } catch (error) {
    console.error('Error creating expense report:', error);
    throw error;
  }
}

export async function updateExpenseReport(id: string, updates: Partial<ExpenseReport>, firestoreUser: User): Promise<void> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('Demo mode: Updating mock expense report', { id });
    await new Promise(resolve => setTimeout(resolve, 300));
    console.log('Mock expense report updated');
    return;
  }

  console.log('API Call: updateExpenseReport', { id, firestoreUser });
  
  try {
    const workspaceId = firestoreUser.workspace?.id;
    if (!workspaceId) {
      throw new Error('No workspace access');
    }

    const reportRef = doc(db, 'workspaces', workspaceId, 'expenses', id);
    await updateDoc(reportRef, updates);
  } catch (error) {
    console.error('Error updating expense report:', error);
    throw error;
  }
}

export async function deleteExpenseReport(id: string, firestoreUser: User): Promise<void> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('Demo mode: Deleting mock expense report', { id });
    await new Promise(resolve => setTimeout(resolve, 300));
    console.log('Mock expense report deleted');
    return;
  }

  console.log('API Call: deleteExpenseReport', { id, firestoreUser });
  
  try {
    const workspaceId = firestoreUser.workspace?.id;
    if (!workspaceId) {
      throw new Error('No workspace access');
    }

    const reportRef = doc(db, 'workspaces', workspaceId, 'expenses', id);
    await deleteDoc(reportRef);
  } catch (error) {
    console.error('Error deleting expense report:', error);
    throw error;
  }
} 